// One-shot hand-off from a link that counts a subset to To review: which
// company, which month, and — for a single payment — which card the person was
// looking at.
//
// Round 8 carried a company from Home to To review only when the fleet pending
// list was capped (the Worker returns the oldest 500 across all companies, so
// that company's cards may be missing from it). Round 10 (C-4) made the rule
// general: every link that counts a subset hands its scope over, so the list
// opens on the cards the link counted. Months hands a company AND a month; the
// Home tile, its Review and "Answer your questions" hand the company; Activity's
// "Go to To review" on a "Needs you" row hands the payment's own reference so
// its card can be opened.
//
// It is a module stash, not a route parameter, because the shell's
// `navigate(target)` carries no scope (integration re-check E5).

import type { CompanySlug } from "../types.js";
import * as face from "./face.js";

/** How the card a person came for is found in the list. */
export interface ReviewCardRef {
  /**
   * Bank rows match the stored bank id; wallet rows match the card's subject;
   * a hub row matches the review-queue card id it names.
   */
  source: "bank" | "moov" | "queue";
  refId: string;
}

/**
 * Which cards a Months blocker line counted. The Worker partitions a month's
 * pending cards into these sets (`ingest-worker/src/months.ts`, the ladder in
 * `getAdminMonths`), so a link that says "4 cards to review" must open those
 * four and not the month's union. Only keys whose `target` is `to_review`.
 */
export const REVIEW_CARD_SETS = [
  "cards_to_review",
  "cards_with_owner",
  "close_blocked",
  "closed_month_write_blocked",
  "reclose_needed",
] as const;
export type ReviewCardSet = (typeof REVIEW_CARD_SETS)[number];

export interface ReviewScopeHint {
  /** Company slug, or null for the whole fleet. */
  company: CompanySlug | null;
  /** `YYYY-MM`, or null for every month. */
  month: string | null;
  /** What the link counted; null when it counted the whole list. */
  set: ReviewCardSet | null;
  /** The one card to open, when the link counted one payment (else null). */
  openCardRef: ReviewCardRef | null;
}

export interface ReviewScopeInput {
  company?: CompanySlug | null;
  month?: string | null;
  set?: ReviewCardSet | null;
  openCardRef?: ReviewCardRef | null;
}

let hint: ReviewScopeHint | null = null;

function clean(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** A period the URL and the API use. Never a display month ("October 2026"). */
function cleanPeriod(value: string | null | undefined): string | null {
  const text = clean(value);
  return text !== null && /^\d{4}-(0[1-9]|1[0-2])$/.test(text) ? text : null;
}

function cleanRef(ref: ReviewCardRef | null | undefined): ReviewCardRef | null {
  const refId = clean(ref?.refId);
  if (refId === null) return null;
  if (ref?.source === "bank" || ref?.source === "moov" || ref?.source === "queue") {
    return { source: ref.source, refId };
  }
  return null;
}

/** An unknown set name is not a set: never filter by a name we do not own. */
function cleanSet(value: ReviewCardSet | null | undefined): ReviewCardSet | null {
  if (typeof value !== "string") return null;
  return (REVIEW_CARD_SETS as readonly string[]).includes(value) ? value : null;
}

/**
 * Record where the person is going. A null argument (or one with nothing in it)
 * clears the stash so a stale scope cannot leak into an unrelated visit.
 */
export function setReviewScopeHint(next: ReviewScopeInput | null): void {
  if (next === null) {
    hint = null;
    return;
  }
  // A company KEY from a hand-off: cleaned with the one face cleaner (it
  // trims and strips the hazards), so the brand guard sees a `face.*` call and
  // the value keeps its `CompanySlug` type (B1c-1).
  const company =
    next.company === null || next.company === undefined
      ? null
      : face.ariaFor(next.company) || null;
  const month = cleanPeriod(next.month);
  const set = cleanSet(next.set);
  const openCardRef = cleanRef(next.openCardRef);
  hint =
    company === null && month === null && set === null && openCardRef === null
      ? null
      : { company, month, set, openCardRef };
}

/** Read and clear the hint. A second call returns null. */
export function takeReviewScopeHint(): ReviewScopeHint | null {
  const value = hint;
  hint = null;
  return value;
}

/**
 * Read the hint without consuming it (rd16 U, C15).
 *
 * The shell paints the header's company picker BEFORE the page renders and
 * takes the hint, so it needs to know which company a hand-off named without
 * stealing the one-shot value the page is about to read.
 */
export function peekReviewScopeHint(): ReviewScopeHint | null {
  return hint;
}

/** Test seam — reset between tests so a stranded stash cannot cross cases. */
export function resetReviewScopeForTests(): void {
  hint = null;
}
